import { useSearchParams } from '@remix-run/react';
import { ArrowDown } from '@solar-icons/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';

import Breadcrumbs from '~/components/breadcrumbs/Breadcrumbs';
import CmsTransformer from '~/components/cms/CmsTransformer';
import CustomPagination from '~/components/pagination/CustomPagination';
import ProductCard from '~/components/ProductCard';
import { useFlashMessages } from '~/context/FlashMessagesContext';
import { useRoutePath } from '~/hooks/useRoutePath';
import Layout from '~/layouts/Default';
import { BrandLoaderData } from '~/pages/BrandPage';
import { Product } from '~/types/Product';
import { apiFetch } from '~/utils/apiFetch';

interface BrandDetailProps {
  serverData: BrandLoaderData;
}

const PER_PAGE = 20;

const BrandDetail: React.FC<BrandDetailProps> = ({ serverData }) => {
  const { t } = useTranslation();
  const routePath = useRoutePath();
  const { addMessage } = useFlashMessages();
  const productsRef = useRef<HTMLDivElement>(null);
  const loadMorePageRef = useRef<number | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  const pageFromUrl = Number(searchParams.get('page')) || 1;

  const lastFetchedPage = useRef(pageFromUrl);

  const [products, setProducts] = useState<Product[]>(serverData.products);
  const [productLoading, setProductLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  const [hasMore, setHasMore] = useState(serverData.hasMore);
  const [numberOfPages, setNumberOfPages] = useState(serverData.numberOfPages);
  const [totalProductsCount, setTotalProductsCount] = useState(serverData.totalProductsCount);

  const API = serverData.API_URL;
  const brand = serverData.brand;

  // Sync state when brand changes (navigating between brands)
  useEffect(() => {
    setProducts(serverData.products);
    setHasMore(serverData.hasMore);
    setNumberOfPages(serverData.numberOfPages);
    setTotalProductsCount(serverData.totalProductsCount);
    lastFetchedPage.current = 1;
  }, [brand.slug, serverData]);

  // Sync currentPage from URL
  useEffect(() => {
    if (loadMorePageRef.current === pageFromUrl) {
      return;
    }
    setCurrentPage(pageFromUrl);
  }, [pageFromUrl]);

  const fetchProducts = useCallback(
    async (page: number, signal?: AbortSignal): Promise<Product[]> => {
      const url = `${API}/api/v2/shop/products/search?limit=${PER_PAGE}&page=${page}&facets[brand][]=${brand.slug}&imageFilter=sylius_shop_product_thumbnail`;
      const response = await apiFetch(url, { signal });
      const data = await response.json();
      setNumberOfPages(data.pagination?.total_pages || 1);
      setHasMore(data.pagination?.has_next_page || false);
      setTotalProductsCount(data.pagination?.total_items || 0);
      return data.products || [];
    },
    [API, brand.slug]
  );

  // Refetch products when page changes via pagination
  useEffect(() => {
    if (lastFetchedPage.current === pageFromUrl) {
      return undefined;
    }

    if (loadMorePageRef.current === pageFromUrl) {
      loadMorePageRef.current = null;
      return undefined;
    }

    lastFetchedPage.current = pageFromUrl;

    const abortController = new AbortController();

    const loadProducts = async () => {
      setProductLoading(true);
      try {
        const productsData = await fetchProducts(pageFromUrl, abortController.signal);
        setProducts(productsData);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        addMessage('error', t('brands.error_load_products'));
        console.error('[brand products load error]', err);
      } finally {
        if (!abortController.signal.aborted) {
          setProductLoading(false);
        }
      }
    };

    loadProducts().catch(console.error);

    return () => abortController.abort();
  }, [pageFromUrl, fetchProducts, addMessage, t]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    try {
      const newProducts = await fetchProducts(nextPage);
      setProducts((prev) => [...prev, ...newProducts]);

      loadMorePageRef.current = nextPage;
      setCurrentPage(nextPage);
      const params = new URLSearchParams(searchParams);
      params.set('page', nextPage.toString());
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState(null, '', newUrl);
    } catch (err) {
      addMessage('error', t('brands.error_load_more'));
      console.error('[brand load more error]', err);
    } finally {
      setLoadingMore(false);
    }
  }, [currentPage, hasMore, loadingMore, searchParams, fetchProducts, addMessage, t]);

  const handlePageChange = (page: number) => {
    if (page === currentPage) return;

    loadMorePageRef.current = null;
    setCurrentPage(page);

    const params = new URLSearchParams(searchParams);

    if (page === 1) {
      params.delete('page');
    } else {
      params.set('page', page.toString());
    }

    setSearchParams(params, { preventScrollReset: true });
    if (productsRef.current) {
      productsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const breadcrumbs = [
    { label: t('common.home'), url: '/' },
    { label: t('brands.title'), url: routePath('brands') },
    { label: brand.name, url: '' },
  ];

  return (
    <Layout>
      <Container className="brand-page">
        <Breadcrumbs paths={breadcrumbs} />
        <div className="brand-header">
          {brand.imagePath && (
            <img
              src={brand.imagePath}
              alt={brand.name}
              className="brand-header__image"
              loading="eager"
              decoding="async"
            />
          )}
          <h1 className={`brand-header__title${brand.hideTitle ? ' visually-hidden' : ''}`}>{brand.name}</h1>
          {brand.description && brand.description.length > 0 && (
            <div className="brand-header__description-wrapper">
              <div className="brand-header__description">
                {brand.description.map((block, index) => (
                  <CmsTransformer item={block} key={index} className="" />
                ))}
              </div>
            </div>
          )}
        </div>

        <div ref={productsRef}>
          <Row className="brand-products">
            {productLoading
              ? Array.from({ length: PER_PAGE }).map((_, i) => (
                  <Col key={i} xs={6} md={4} lg={3} className="brand-products__item">
                    <Skeleton height={300} />
                  </Col>
                ))
              : products.map((product, index) => (
                  <Col key={product.id} xs={6} md={4} lg={3} className="brand-products__item">
                    <ProductCard product={product} lazyloaded={index >= 2} />
                  </Col>
                ))}
          </Row>
          {products.length > 0 && !productLoading && (
            <div className="category-footer">
              {!loadingMore && hasMore && (
                <Button variant="outline-primary" onClick={loadMore}>
                  <span>
                    {t('common.load_more')} ({Math.min(PER_PAGE, totalProductsCount - products.length)})
                  </span>{' '}
                  <ArrowDown weight="Linear" size={18} />
                </Button>
              )}
            </div>
          )}
          {loadingMore && (
            <div className="text-center mt-4">
              <div className="spinner-border text-primary" role="status" />
            </div>
          )}
          {products.length > 0 && !productLoading && (
            <CustomPagination numberOfPages={numberOfPages} currentPage={currentPage} onPageChange={handlePageChange} />
          )}
          {products.length === 0 && !productLoading && (
            <div className="text-center h3 mt-5">
              <strong>{t('brands.no_products')}</strong>
            </div>
          )}
        </div>
      </Container>
    </Layout>
  );
};

export default BrandDetail;
